import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { FieldError, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiChevronUp } from 'react-icons/bi';
import { FaRegTrashAlt } from 'react-icons/fa';
import { TiWarning } from 'react-icons/ti';
import { boolean, InferType, number, object, string } from 'yup';

import { IntervalType } from '@client/api';
import {
  Button,
  CurrencyAdornment,
  CurrencyAutocomplete,
  CurrencyCode,
  FormField,
  getCurrencySymbol,
  GridCol,
  GridRow,
  IconButton,
  Input,
  isDefinedNumber,
  isDefinedString,
  Label,
  Text,
  toCurrencyStringOrUndefined,
  transformEmptyStringToUndefined,
  transformToOptionalNumber,
} from '@client/toolkit';
import { Disclosure } from '@headlessui/react';

import { ProductFormData } from './product.form';

import { useTenantSettingsDefaults } from '../../../../hooks';

export type IntervalOption = 'Monthly' | 'EveryThreeMonth' | 'EverySixMonth' | 'Yearly' | 'Custom';

const schema = object({
  priceId: string().transform(transformEmptyStringToUndefined),
  unitAmount: number().transform(transformToOptionalNumber).required(),
  unitCurrencyCode: string().required(),
  description: string(),
  hasInterval: boolean().default(false),
  intervalOption: string<IntervalOption>(),
  intervalType: string<IntervalType>().when('hasInterval', {
    is: (x: boolean) => !!x,
    then: (schema) => schema.required(),
  }),
  intervalCount: number()
    .positive()
    .integer()
    .transform(transformToOptionalNumber)
    .when('hasInterval', {
      is: (x: boolean) => !!x,
      then: (schema) => schema.required(),
    }),
});

type FormValues = InferType<typeof schema>;

export const productPriceSchema = schema;
export type ProductPriceFormData = FormValues;

interface ProductPriceItemProps {
  index: number;
  onRemove: (index: number) => void;
  hideRemove?: boolean;
}

export function ProductPriceItem({ index, onRemove, hideRemove }: ProductPriceItemProps) {
  const { t, ready } = useTranslation('products');
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const { defaultCurrencyCode } = useTenantSettingsDefaults();

  const { getFieldState, setValue, control } = useFormContext<ProductFormData>();
  const defaultIntervalCount = 1;
  const defaultIntervalType = 'Month';

  const unitCurrencyCode = useWatch({ control, name: `prices.${index}.unitCurrencyCode`, defaultValue: '' });
  const unitAmount = useWatch({ control, name: `prices.${index}.unitAmount`, defaultValue: 0 });
  const hasInterval = useWatch({ control, name: `prices.${index}.hasInterval`, defaultValue: false });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const intervalType = useWatch({ control, name: `prices.${index}.intervalType`, defaultValue: '' as any });
  const intervalCountString = useWatch({ control, name: `prices.${index}.intervalCount`, defaultValue: undefined }) as
    | number
    | string
    | undefined;

  const intervalCount = useMemo(() => {
    if (typeof intervalCountString === 'string') {
      if (isNaN(Number(intervalCountString)) || intervalCountString.trim() === '') {
        return undefined;
      }
      return Number(intervalCountString);
    } else if (typeof intervalCountString === 'number') {
      return intervalCountString;
    }

    return undefined;
  }, [intervalCountString]);

  const unitCurrencySymbol =
    isDefinedString(unitCurrencyCode) && isDefinedString(getCurrencySymbol(unitCurrencyCode))
      ? getCurrencySymbol(unitCurrencyCode)
      : undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errorMessages: { [key: string]: FieldError } | undefined = getFieldState(`prices.${index}`)?.error as any;
  const hasAnyError = Object.keys(errorMessages || {}).length > 0;

  const handleSelectHasInterval = useCallback(
    (newHasInterval = false) => {
      if (hasInterval === newHasInterval) {
        return;
      }
      setValue(`prices.${index}.hasInterval`, newHasInterval, { shouldDirty: true });
      if (!newHasInterval) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setValue(`prices.${index}.intervalOption`, undefined as any, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setValue(`prices.${index}.intervalCount`, undefined as any, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setValue(`prices.${index}.intervalType`, undefined as any, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      } else {
        setValue(`prices.${index}.intervalCount`, defaultIntervalCount, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
        setValue(`prices.${index}.intervalType`, defaultIntervalType, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    },
    [hasInterval, index, setValue],
  );

  return (
    <Disclosure defaultOpen>
      {({ open }) => (
        <div
          className={classNames(
            'bg-surface-base rounded-lg border border-border',
            !open && hasAnyError && 'border-red-500',
          )}
        >
          <Disclosure.Button as="div" className="py-2 px-4 flex justify-between items-center cursor-pointer space-x-4">
            <div className="flex-1 text-sm">
              {open ? (
                <Text loading={!ready} className="font-medium">
                  {t('product.priceOverview')}
                </Text>
              ) : (
                <div className="flex items-center">
                  {!open && hasAnyError && <TiWarning className="text-lg w-5 h-5 inline-block mr-1 text-red-500" />}
                  <div className="text-primary">
                    <Text
                      isEmpty={!isDefinedNumber(unitAmount)}
                      emptyValue={toCurrencyStringOrUndefined(unitAmount, undefined, {
                        currency: (unitCurrencyCode as CurrencyCode) || 'EUR',
                      })}
                    >
                      {toCurrencyStringOrUndefined(unitAmount, undefined, {
                        currency: (unitCurrencyCode as CurrencyCode) || 'EUR',
                      })}
                    </Text>
                    {hasInterval && (
                      <Text
                        className="ml-1"
                        isEmpty={!isDefinedNumber(intervalCount) || !isDefinedString(intervalType)}
                        emptyValue=""
                      >
                        {tGlobal('price.intervalDescription', {
                          count: intervalCount,
                          type: intervalType,
                        })}
                      </Text>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center">
              {!hideRemove && (
                <Button size="xs" color="primary-invert" onClick={() => onRemove(index)}>
                  <FaRegTrashAlt />
                </Button>
              )}
              <IconButton size="sm" className="ml-2 -mr-2">
                <BiChevronUp className={`${open ? 'rotate-180 transform' : ''} h-6 w-6 text-primary`} />
              </IconButton>
            </div>
          </Disclosure.Button>
          <Disclosure.Panel className="pb-4 pt-2 px-4">
            <GridRow cols={{ def: 1, sm: 3 }} spacingX={2} spacingY={4}>
              {/** Pricing (price + currency) */}
              <GridCol span={{ def: 1, sm: 2 }}>
                <FormField
                  name={`prices.${index}.unitAmount`}
                  defaultValue={''}
                  label={tGlobal('price.unitAmount')}
                  required
                  render={({ field }) => (
                    <CurrencyAdornment position="start" symbol={unitCurrencySymbol}>
                      <Input
                        type="number"
                        className={
                          unitCurrencySymbol &&
                          (typeof unitCurrencySymbol === 'string' && unitCurrencySymbol.length >= 3
                            ? '!pl-11'
                            : '!pl-6')
                        }
                        {...field}
                      />
                    </CurrencyAdornment>
                  )}
                />
              </GridCol>
              <GridCol span={1}>
                <FormField
                  name={`prices.${index}.unitCurrencyCode`}
                  label={tGlobal('price.unitCurrency')}
                  defaultValue={''}
                  required
                  render={({ field: { value, ref, ...field } }) => (
                    <CurrencyAutocomplete selected={value} defaultCurrencyCode={defaultCurrencyCode} {...field} />
                  )}
                />
              </GridCol>

              {/** Toggle hasInterval */}
              <GridCol span={3}>
                <Label className="mb-1">{tGlobal('price.type')}</Label>
                <div className="flex items-center space-x-2">
                  <Button
                    size="none"
                    className="px-4 py-1 text-sm rounded-lg"
                    color={hasInterval ? 'primary' : 'primary-light'}
                    onClick={() => handleSelectHasInterval(true)}
                  >
                    {tType('PriceType.Recurring')}
                  </Button>
                  <Button
                    size="none"
                    className="px-4 py-1 text-sm rounded-lg"
                    color={!hasInterval ? 'primary' : 'primary-light'}
                    onClick={() => handleSelectHasInterval(false)}
                  >
                    {tType('PriceType.OneTime')}
                  </Button>
                </div>
              </GridCol>
              {/** Description */}
              <GridCol span={3}>
                <FormField
                  name={`prices.${index}.description`}
                  label={tGlobal('price.description')}
                  defaultValue={''}
                  render={({ field }) => <Input {...field} />}
                />
              </GridCol>
            </GridRow>
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
}
